import _ from 'underscore';
import React, { useContext, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Text, Subtitle1 } from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ShortHeadlinesPanel,
  DescriptionsPanel,
  PANEL_KEYS,
  ImagesPanel,
  BaseImagePanel,
  LogosPanel,
  DateSettingsPanel,
  AudienceSignalSection } from '@bingads-webui-campaign-react/wizard-shared-components';
import { VideoPanel, AudienceSignalPanel } from '@bingads-webui-campaign-react/step-wizard-controls';
import {
  useCollapseState,
  Collapse,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import {
  TextAssetsImg,
  ImageAssetsImg,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { useVideoAdsCopilot } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { TaskManager } from '@bingads-webui-universal/task-manager';
import { useWizardService } from '@bingads-webui-react/wizard';
import { FLOWS } from '@bingads-webui-campaign-react/unified-app-shared';
import { getHeadlinesCardSummaryDetails, getDescriptionsCardSummaryDetails } from '@bingads-webui-campaign-react/validated-text-collection';

export const AssetsSection = ({
  imageExperiencePortalContainerState,
  transparentLogoExperiencePortalContainerState,
  imageExpereienceProngPortalContainerState,
  imageExperienceSpotLightPortalContainerState,
  prongImageExperienceAddNewImageCallback,
  imageSpotlightExperienceAddNewImageCallback,
  transparentLogoContainerRef,
  logosContainerRef,
  imageExperienceAddNewImageCallback,
  imagesContainerRef,
  imageSpotlightContainerRef,
  imageProngContainerRef,
  setAddNewDescriptionCallback,
  setAddNewShortHeadlinesCallback,
  perfMarker,
  mergedI18n,
  data,
  assetPanels,
  setSelectedVideoId,
  selectedVideoId,
}) => {
  const isLoading = useMemo(() => _.isEmpty(data), [data]);

  const i18n = useContext(I18nContext);

  const { state } = useWizardService();

  const {
    wizardData: { flow },
  } = state.context;

  const {
    Headlines,
    Descriptions,
  } = data || {};

  const headlinesCardSummaryDetails = useMemo(() => getHeadlinesCardSummaryDetails(i18n, Headlines), [Headlines, i18n]);
  const descriptionsCardSummaryDetails = useMemo(() => getDescriptionsCardSummaryDetails(i18n, Descriptions), [Descriptions, i18n]);

  const [
    allSections,
    allSectionsExpandedSections,
    setAllSectionsExpandedSections,
  ] = useCollapseState(
    assetPanels /* all sections */,
    assetPanels /* initially expanded sections */
  );

  const [
    moreSettings,
    moreSettingsExpandedSections,
    setMoreSettingsExpandedSections,
  ] = useCollapseState(
    PANEL_KEYS.START_END_DATES /* all sections */,
    [] /* initially expanded sections */
  );

  const allAssetsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAllSectionsExpandedSections }),
    [setAllSectionsExpandedSections]
  );

  const { fetch } = useVideoAdsCopilot();

  const copilotFetchTaskManager = useMemo(() => new TaskManager(), []);

  useEffect(() => {
    fetch(copilotFetchTaskManager);
    // setClarityTag({ VideoCopilotGeneration: true });
  }, [copilotFetchTaskManager, fetch]);

  return (
    <div className="assets-section">
      <div style={{ marginTop: 10, marginBottom: 10 }}>
        <div>
          <Subtitle1>
            {i18n.getString(_TL_('Add assets'))}
          </Subtitle1>
        </div>
        <div style={{ marginTop: 5 }}>
          <Text>
            {i18n.getString(_TL_('Provide your assets and we\'ll automatically determine what your ads look like, where and when they appear, and who sees them.'))}
          </Text>
        </div>
      </div>
      <div>
        <Collapse
          allPanelItems={allSections}
          skipItemsTypeCheck
          activeKeys={allSectionsExpandedSections}
          setActiveKeys={setAllSectionsExpandedSections}
          checkItems
        >
          <ImagesPanel
            key={PANEL_KEYS.IMAGES}
            allAssetsExpand={allAssetsExpand}
            setAddNewImageCallback={() => {}}
            icon={ImageAssetsImg}
            imagesContainerRef={imagesContainerRef}
            imageExperiencePortalContainerState={imageExperiencePortalContainerState}
            imageExperienceAddNewImageCallback={imageExperienceAddNewImageCallback}
            images={data && data.Images}
            topicKey="pop_BA_Apps_Images"
          />
          <BaseImagePanel
            key={PANEL_KEYS.SPOTLIGHT_IMAGES}
            allAssetsExpand={allAssetsExpand}
            setAddNewImageCallback={() => {}}
            icon={ImageAssetsImg}
            titleKey={_TL_('Large image')}
            iconAltText={_TL_('"Images" panel\'s image')}
            imagesData={data && data.ImageSPOTLIGHT}
            containerRef={imageSpotlightContainerRef}
            portalContainerState={imageExperienceSpotLightPortalContainerState}
            addNewImageCallback={imageSpotlightExperienceAddNewImageCallback}
            maxUploadNum={1}
          />
          <BaseImagePanel
            key={PANEL_KEYS.PRONG_IMAGES}
            allAssetsExpand={allAssetsExpand}
            setAddNewImageCallback={() => {}}
            icon={ImageAssetsImg}
            titleKey={_TL_('Small image')}
            iconAltText={_TL_('"Images" panel\'s image')}
            imagesData={data && data.ImagePRONG}
            containerRef={imageProngContainerRef}
            portalContainerState={imageExpereienceProngPortalContainerState}
            addNewImageCallback={prongImageExperienceAddNewImageCallback}
            maxUploadNum={1}
          />
          <LogosPanel
            key={PANEL_KEYS.LOGOS}
            allAssetsExpand={allAssetsExpand}
            icon={ImageAssetsImg}
            titleKey={flow !== FLOWS.WINDOWS ? _TL_('Logos') : _TL_('App logo')}
            logos={data && data.logos}
            logosContainerRef={flow !== FLOWS.WINDOWS ? logosContainerRef : transparentLogoContainerRef}
            logoExperiencePortalContainerState={flow !== FLOWS.WINDOWS ? imageExperiencePortalContainerState : transparentLogoExperiencePortalContainerState}
            description={
              flow !== FLOWS.WINDOWS
              ? _TL_('You can add up to 5 different logos. Each logo can have one or both 4:1 aspect ratio and 1:1 aspect ratio.')
              : _TL_('Add one logo with a transparent background. We require PNG format and an aspect ratio of 1 : 1. The minimum dimensions are 300 x 300 pixels, and the maximum dimensions are 1000 x 1000 pixels.')}
          />
          <ShortHeadlinesPanel
            key={PANEL_KEYS.SHORT_HEADLINES}
            allAssetsExpand={allAssetsExpand}
            icon={TextAssetsImg}
            featureName={flow === FLOWS
              .WINDOWS ? 'windowsStoreAds' : 'unifiedAppAds'}
            setAddNewShortHeadlinesCallback={setAddNewShortHeadlinesCallback}
            isOptional={flow === FLOWS.WINDOWS}
            headlinesCardSummaryDetails={headlinesCardSummaryDetails}
          />
          <DescriptionsPanel
            key={PANEL_KEYS.DESCRIPTIONS}
            allAssetsExpand={allAssetsExpand}
            icon={TextAssetsImg}
            featureName="unifiedAppAds"
            setAddNewDescriptionCallback={setAddNewDescriptionCallback}
            descriptionsCardSummaryDetails={descriptionsCardSummaryDetails}
          />
          {flow !== FLOWS.WINDOWS && (
            <VideoPanel
              key={PANEL_KEYS.VIDEOS}
              perfMarker={perfMarker}
              mergedI18n={mergedI18n}
              videos={data && data.videos}
              icon={TextAssetsImg}
              allAssetsExpand={allAssetsExpand}
              isVideoRequired
              topicKey="pop_BA_Apps_Videos"
              skipQualityValidation
              setSelectedVideoId={setSelectedVideoId}
              selectedVideoId={selectedVideoId}
            />
          )}
        </Collapse>
      </div>
      <div style={{ marginTop: 10, marginBottom: 10 }}>
        {
            _.contains(assetPanels, PANEL_KEYS.AUDIENCE_SIGNAL) && (
              <AudienceSignalSection>
                <Collapse
                  allPanelItems={allSections}
                  checkItems
                  skipItemsTypeCheck
                  activeKeys={allSectionsExpandedSections}
                  setActiveKeys={setAllSectionsExpandedSections}
                >
                  <AudienceSignalPanel
                    key={PANEL_KEYS.AUDIENCE_SIGNAL}
                    isLoading={isLoading}
                    options={{
                      wizardPerfMarker: perfMarker,
                    }}
                    expand={allAssetsExpand}
                    data={data}
                  />
                </Collapse>
              </AudienceSignalSection>
            )
          }
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={{ marginTop: 5, marginBottom: 5 }}>
          <Text size={400} weight="bold"> {i18n.getString(_TL_('More Settings'))} </Text>
        </div>
        <Collapse
          allPanelItems={moreSettings}
          skipItemsTypeCheck
          activeKeys={moreSettingsExpandedSections}
          setActiveKeys={setMoreSettingsExpandedSections}
          checkItems
        >
          <DateSettingsPanel
            isLoading={isLoading}
            key={PANEL_KEYS.START_END_DATES}
            expand={allAssetsExpand}
            data={data}
          />
        </Collapse>
      </div>
    </div>
  );
};

AssetsSection.propTypes = {
  imageExperiencePortalContainerState: PropTypes.shape({}).isRequired,
  imageExperienceAddNewImageCallback: PropTypes.func.isRequired,
  imageProngContainerRef: PropTypes.shape({}).isRequired,
  imageSpotlightContainerRef: PropTypes.shape({}).isRequired,
  imageExpereienceProngPortalContainerState: PropTypes.shape({}).isRequired,
  imageExperienceSpotLightPortalContainerState: PropTypes.shape({}).isRequired,
  logosContainerRef: PropTypes.shape({}).isRequired,
  imagesContainerRef: PropTypes.shape({}).isRequired,
  setAddNewDescriptionCallback: PropTypes.func.isRequired,
  setAddNewShortHeadlinesCallback: PropTypes.func.isRequired,
  perfMarker: PropTypes.string.isRequired,
  mergedI18n: PropTypes.shape({}).isRequired,
  prongImageExperienceAddNewImageCallback: PropTypes.func.isRequired,
  imageSpotlightExperienceAddNewImageCallback: PropTypes.func.isRequired,
  data: PropTypes.shape({
    videos: PropTypes.shape([]),
    Images: PropTypes.shape([]),
    ImageSPOTLIGHT: PropTypes.shape([]),
    ImagePRONG: PropTypes.shape([]),
    logos: PropTypes.shape([]),
  }).isRequired,
  transparentLogoContainerRef: PropTypes.shape({}).isRequired,
  transparentLogoExperiencePortalContainerState: PropTypes.shape({}).isRequired,
  assetPanels: PropTypes.shape({}).isRequired,
  setSelectedVideoId: PropTypes.func.isRequired,
  selectedVideoId: PropTypes.string.isRequired,
};
